import React, {useCallback, useState} from 'react';

import {UserLink} from 'src/components/links';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Tooltip} from '@pb/kit-component-popups';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {useUserDetailsDrawer} from '../../user-details-drawer/use-user-details-drawer';
import {actionsCell} from '../../user-details-drawer/user-details-drawer.module.styl';

import {GroupMembersSkeleton} from './group-members.skeleton';
import {UserSuggest} from './user-suggest';

import type {UpdateGroupMembersRequest} from '../typedef';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MemberInfo} from 'src/openapi/admin/models/MemberInfo';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


const UMNATCHED_EMAIL_TOOLTIP = 'No user found matching this email address. Please remove and try again.';
const ADD_MEMBER_INFO = 'Search for user in account or paste some emails from clipboard';

type Props = {
    accountId: number,
    isLoading: boolean,
    loadingError: Error | null,
    members: Array<MemberInfo>,
    renderedUsers: Array<MemberInfo & {view: string}>,
    requestInfo: UpdateGroupMembersRequest,
    setRequestInfo: React.Dispatch<React.SetStateAction<UpdateGroupMembersRequest>>,
    viewType: 'New' | 'Existing',
};

export const GroupMembersTab = (props: Props) => {
    const {
        accountId,
        isLoading,
        loadingError,
        members,
        requestInfo,
        setRequestInfo,
        renderedUsers,
        viewType,
    } = props;

    const {hasAccess} = useAdminAccessRight();
    const editEnabled = hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_EDIT);
    const {groupChange} = useEventTracking();

    const [accessOverrides, setAccessOverrides] = useState<Record<number, Pick<MemberInfo, 'typeCode' | 'statusCode'>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    const {allowed: drawerEnabled, selectedUserId, getRefTable, renderAction, drawer} =
        useUserDetailsDrawer(viewType === 'Existing', (userId, details) => {
            setAccessOverrides(values => ({...values,
                [userId]: {typeCode: details.typeCode, statusCode: details.statusCode}}));
        });

    const onAddMember = useCallback((member: MemberInfo) => {
        if (requestInfo.removeMembers?.find(({userId}) => userId === member.userId)) {
            setRequestInfo({
                ...requestInfo,
                removeMembers: requestInfo.removeMembers?.filter(({userId}) => userId !== member.userId),
            });
        } else {
            setRequestInfo({
                ...requestInfo,
                addMembers: requestInfo.addMembers ? [...requestInfo.addMembers, member] : [member],
            });
        }

        groupChange({viewType, parameterName: 'Add Member'});
    }, [requestInfo, viewType, groupChange, setRequestInfo]);

    const onAddMembers = useCallback((bulkMembers: Array<MemberInfo>) => {
        if (bulkMembers.length) {
            setRequestInfo({
                ...requestInfo,
                addMembers: requestInfo.addMembers ? [...requestInfo.addMembers, ...bulkMembers] : bulkMembers,
            });

            groupChange({viewType, parameterName: 'Add Member'});
        }
    }, [requestInfo, viewType, groupChange, setRequestInfo]);

    const onRemoveMember = useCallback((userEmail: string) => {
        const existedMember = members.find(user => userEmail === user.email);
        const addedMembersBuffer = requestInfo.addMembers?.filter(user => userEmail !== user.email);

        setRequestInfo({
            removeMembers: [
                ...requestInfo.removeMembers || [],
                ...existedMember ? [existedMember] : [],
            ],
            addMembers: addedMembersBuffer,
        });

        groupChange({viewType, parameterName: 'Remove Member'});
    }, [requestInfo, members, viewType, groupChange, setRequestInfo]);

    return (
        <div ref={getRefTable}>
            <WidthItem width={827}>
                <Skeleton shown={isLoading} view={<GroupMembersSkeleton/>}>
                    <ServerResponseHandler
                        serverError={loadingError}
                        accessRight={ACCESS_RIGHTS.GROUP_SCREEN_VIEW as AccessRight}>
                        <Group direction="v" alignItems="stretch">
                            {editEnabled ? (
                                <div>
                                    <Label><Info type={{popover: ADD_MEMBER_INFO}}>Add Members</Info></Label>
                                    <UserSuggest
                                        accountId={accountId}
                                        memberIds={members
                                            .filter(({userId}) => !requestInfo
                                                .removeMembers?.some(({userId: id}) => id === userId))
                                            .map(({userId}) => userId)}
                                        key={`${requestInfo.addMembers?.length}  ${requestInfo.removeMembers?.length}`}
                                        addedMembers={requestInfo.addMembers || []}
                                        onAdd={onAddMember}
                                        onBulkAdd={onAddMembers}
                                    />
                                </div>
                            ) : null}
                            {!!renderedUsers.length && (
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell width={240}>Email</Cell>
                                        <Cell width={drawerEnabled ? 130 : 150}>First Name</Cell>
                                        <Cell width={drawerEnabled ? 130 : 150}>Last Name</Cell>
                                        <Cell width={120}>Type</Cell>
                                        <Cell width={120}>Status</Cell>
                                        <Cell
                                            width={drawerEnabled ? 80 : 40}
                                            className={drawerEnabled ? actionsCell : undefined}>
                                            {drawerEnabled ? 'Actions' : null}
                                        </Cell>
                                    </Head>
                                    <Body>
                                        {renderedUsers
                                            .map(user => ({...user, ...accessOverrides[user.userId]}))
                                            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                            .map(({view, ...user}) => {
                                                const isUnmatched = !user.userId;
                                                return (
                                                    <Row key={user.email} highlighted={user.userId === selectedUserId}>
                                                        <Cell>
                                                            <Group>
                                                                {view === 'Added' && isUnmatched ? (
                                                                    <Tooltip
                                                                        color="light"
                                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                                            <span ref={anchorRef} {...getAnchorProps()}>
                                                                                <Icon type={Icons.ERROR}/>
                                                                            </span>
                                                                        )}>
                                                                        {UMNATCHED_EMAIL_TOOLTIP}
                                                                    </Tooltip>
                                                                ) : null}
                                                                <WidthItem width={220}>
                                                                    {user.userId ? (
                                                                        <UserLink
                                                                            userId={user.userId}
                                                                            caption={user.email}
                                                                            place="Group Members"
                                                                        />
                                                                    ) : (
                                                                        <EllipsisTextHint title={user.email}>
                                                                            {user.email || '--'}
                                                                        </EllipsisTextHint>
                                                                    )}
                                                                </WidthItem>
                                                            </Group>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint title={user.firstName}>
                                                                {user.firstName || '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint title={user.lastName}>
                                                                {user.lastName || '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint
                                                                title={getUserTypeByCode(user.typeCode)?.description ||
                                                                    '--'}>
                                                                {getUserTypeByCode(user.typeCode)?.description || '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint
                                                                title={getUserStatusByCode(
                                                                    user.statusCode,
                                                                )?.description || '--'}>
                                                                {getUserStatusByCode(user.statusCode)?.description ||
                                                                    '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell className={drawerEnabled ? actionsCell : undefined}>
                                                            <Group gap="s" alignItems="center">
                                                                {user.userId ? renderAction(user.userId) : null}
                                                                {editEnabled ? (
                                                                    <div style={{paddingTop: 4}}>
                                                                        <Tooltip
                                                                            color="light"
                                                                            anchor={({anchorRef, getAnchorProps}) => (
                                                                                <BasicIconButton
                                                                                    ref={anchorRef}
                                                                                    {...getAnchorProps()}
                                                                                    iconProps={{type: Icons.TRASH}}
                                                                                    onClick={() => {
                                                                                        onRemoveMember(user.email);
                                                                                    }}
                                                                                />
                                                                            )}>
                                                                            Remove member
                                                                        </Tooltip>
                                                                    </div>
                                                                ) : null}
                                                            </Group>
                                                        </Cell>
                                                    </Row>
                                                );
                                            })}
                                    </Body>
                                </Table>
                            )}
                            <Item grow={1}>
                                {!!renderedUsers && (
                                    <PaginationStripe
                                        pageSize={pageSize}
                                        totalCount={renderedUsers.length}
                                        currentPage={currentPage}
                                        onPaginationChange={(pagination: Pagination) => {
                                            setCurrentPage(pagination.currentPage);
                                            setPageSize(pagination.pageSize);
                                        }}
                                        goToPage={true}
                                        sizeSwitcherPosition="LEFT"
                                        sizeSwitcherType="ONLY_PAGE_SIZE"
                                        sizes={[5, 10]}
                                    />
                                )}
                            </Item>
                        </Group>
                    </ServerResponseHandler>
                </Skeleton>
            </WidthItem>
            {drawer}
        </div>
    );
};
